// axiosInstance.js

import axios from 'axios';
import { BASE_URL, BASE_URL2 } from './axios';

// Raw un-intercepted axios instance for internal auth operations (prevents recursive interceptor loops)
const rawAxios = axios.create();

/**
 * Retrieve current access token from storage
 */
const getAccessToken = () => {
  return sessionStorage.getItem("access_token") || localStorage.getItem("access_token");
};

/**
 * Retrieve current refresh token from storage
 */
const getRefreshToken = () => {
  return sessionStorage.getItem("refresh_token") || localStorage.getItem("refresh_token");
};

/**
 * Helper to update stored tokens and re-sync encryption key
 */
const setTokens = (accessToken, refreshToken) => {
  if (accessToken) {
    sessionStorage.setItem("access_token", accessToken);
    if (localStorage.getItem("access_token")) {
      localStorage.setItem("access_token", accessToken);
    }
  }
  if (refreshToken) {
    sessionStorage.setItem("refresh_token", refreshToken);
    if (localStorage.getItem("refresh_token")) {
      localStorage.setItem("refresh_token", refreshToken);
    }
  }
};

/**
 * Handle auth failure: clear tokens and redirect to login
 */
const handleAuthFailure = () => {
 
  sessionStorage.clear();
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");

  if (typeof window !== "undefined") {
    const currentPath = window.location.pathname;
    // if (!currentPath.includes("/login") && currentPath !== "/") {
    //   window.location.href = FRONTEND_URL || "/";
    // }
  }
};

/**
 * Refresh access token by calling the backend refresh route:
 * Route: POST ${BASE_URL}/user/refresh-token/
 * Headers: Authorization: Bearer ${refreshToken}
 * Body: { refresh: refreshToken, refresh_token: refreshToken }
 */
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await rawAxios.post(
    `${BASE_URL}/user/refresh-token/`,
    {
      refresh: refreshToken,
      refresh_token: refreshToken,
    },
    {
      headers: {
        Authorization: `Bearer ${refreshToken}`,
        "Content-Type": "application/json",
        accept: "application/json",
      },
    }
  );

  const newAccessToken = response.data?.access_token || response.data?.access;
  if (!newAccessToken) {
    throw new Error("No access token returned from refresh endpoint");
  }

  const newRefreshToken = response.data?.refresh_token || response.data?.refresh;
  setTokens(newAccessToken, newRefreshToken);

  return newAccessToken;
};

// Queue handling for concurrent requests during token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
});
/**
 * Attach request and response interceptors to an axios instance
 */
const applyAuthInterceptors = (instance) => {
  // Request Interceptor: inject Bearer token
  instance.interceptors.request.use(
    (config) => {
      const token = getAccessToken();
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization && !config.headers.authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response Interceptor: auto refresh on 401
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (!error.response || !originalRequest) {
        return Promise.reject(error);
      }

      // If status is 401 Unauthorized and request has not been retried yet
      if (error.response.status === 401 && !originalRequest._retry) {
        // Prevent infinite loops if refresh token endpoint itself returned 401
        if (originalRequest.url && originalRequest.url.includes("/user/refresh-token/")) {
          handleAuthFailure();
          return Promise.reject(error);
        }

        // If another request is already refreshing token, queue this one
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newAccessToken = await refreshAccessToken();

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);

          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          handleAuthFailure();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};


// Apply interceptors to axiosInstance
applyAuthInterceptors(axiosInstance);

// Also apply to global axios so existing calls in the application automatically get the interceptor
applyAuthInterceptors(axios);
// Apply interceptors to axiosInstance
applyAuthInterceptors(axiosInstance);

// Also apply to global axios so existing calls in the application automatically get the interceptor
applyAuthInterceptors(axios);

const axiosInstance2 = axios.create({
  baseURL: BASE_URL2,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
});

// FIX: axiosInstance2 ko bhi token + auto-refresh interceptors chahiye
applyAuthInterceptors(axiosInstance2);


export {
  BASE_URL,
  axiosInstance2,
  axiosInstance,
  axiosInstance as apiClient,
  refreshAccessToken,
    handleAuthFailure,
  getAccessToken,
  getRefreshToken,
  setTokens,
  applyAuthInterceptors,
};




